import { useState, useEffect, useCallback, useMemo } from 'react';
import { roundQuantity, debounce } from '@teko/utils';

const DELAY = 300;
const useQuantity = ({
  addToCart,
  baseQuantity = 0,
  precision = 0
}) => {
  const [quantity, setQuantity] = useState((addToCart == null ? void 0 : addToCart.quantityInCart) || baseQuantity);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (addToCart && !loading) {
      setQuantity(addToCart.quantityInCart || baseQuantity);
    }
  }, [addToCart]);
  const updateQuantity = useCallback(async (quantity2) => {
    var _a;
    setLoading(true);
    const normalizedQuantity = roundQuantity({
      quantity: quantity2,
      precision
    });
    if (normalizedQuantity === 0) {
      await ((_a = addToCart == null ? void 0 : addToCart.onRemoveItem) == null ? void 0 : _a.call(addToCart));
    } else {
      await (addToCart == null ? void 0 : addToCart.onChangeQuantity(normalizedQuantity));
    }
    setLoading(false);
  }, [addToCart, precision]);
  const handleUpdateQuantity = async () => {
    if (quantity && quantity !== (addToCart == null ? void 0 : addToCart.quantityInCart)) {
      await updateQuantity(quantity);
    } else {
      setQuantity((addToCart == null ? void 0 : addToCart.quantityInCart) || baseQuantity);
    }
  };
  const debounceChange = useMemo(() => {
    const debounceUpdate = debounce(async (quantity2, onError) => {
      try {
        await updateQuantity(quantity2);
      } catch (error) {
        onError == null ? void 0 : onError(error);
      }
    }, DELAY);
    return (newQuantity) => {
      setQuantity(newQuantity);
      debounceUpdate(newQuantity, () => setQuantity((addToCart == null ? void 0 : addToCart.quantityInCart) || baseQuantity));
    };
  }, [addToCart == null ? void 0 : addToCart.quantityInCart, baseQuantity, updateQuantity]);
  const onIncrease = () => {
    debounceChange(quantity + 1);
  };
  const onDecrease = () => {
    debounceChange(quantity - 1);
  };
  return {
    quantity,
    setQuantity,
    handleUpdateQuantity,
    onIncrease,
    onDecrease
  };
};

export { useQuantity };
